import { useCallback, useEffect, useMemo, useState } from 'react'
import { CircleHelp, Settings2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentCastOverride,
} from 'app/frontend/types/custom/RaidPlan'
import { v4 as uuid } from 'uuid'
import {
  getSoakPrefix,
  normalizeCustomGrouping,
  sanitizeCustomGroupingInput,
} from './assignmentTimeline'
import TimelineAbilitySection from './TimelineAbilitySection'
import TimelineContainer from './TimelineContainer'
import SpellIcon from './SpellIcon'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import { formatTimelineTimestamp } from './timelineMath'
import { EncounterAbilityRow } from '../hooks/useTeamPlanComputedData'
import { FormGroup } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import {
  ColorPicker,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))
const createCastOverrideId = () => uuid().replaceAll('-', '_')

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  row: EncounterAbilityRow
  ability: RaidPlanEncounterAssignmentAbility
  spellName: string
  defaultTitle: string
  canEdit: boolean
  assignmentGroups: number
  onUpdate: (
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory?: boolean,
  ) => void
  onCommit: () => void
}

const TeamPlanAssignmentDialog = ({
  open,
  onOpenChange,
  row,
  ability,
  spellName,
  defaultTitle,
  canEdit,
  assignmentGroups,
  onUpdate,
  onCommit,
}: Props) => {
  const { t } = useTranslation()
  const { computed, zoom } = useTeamPlanTimeline()
  const [customGroupingDraft, setCustomGroupingDraft] = useState(
    ability.custom_grouping ?? '',
  )
  const [isEditingCustomGrouping, setIsEditingCustomGrouping] = useState(false)
  const [assignmentGroupsDraft, setAssignmentGroupsDraft] = useState(
    assignmentGroups.toString(),
  )
  const [isEditingAssignmentGroups, setIsEditingAssignmentGroups] =
    useState(false)
  const [draggingCastId, setDraggingCastId] = useState<string | null>(null)

  const sanitizeAssignmentGroupsInput = (value: string) =>
    value.replace(/[^\d]/g, '')

  const normalizeAssignmentGroups = (value: string) => {
    const sanitized = sanitizeAssignmentGroupsInput(value)
    if (!sanitized) return undefined
    const parsed = Number(sanitized)
    if (!Number.isInteger(parsed)) return undefined
    return Math.min(Math.max(parsed, 1), 20)
  }

  useEffect(() => {
    if (isEditingCustomGrouping) return
    setCustomGroupingDraft(ability.custom_grouping ?? '')
  }, [ability.custom_grouping, isEditingCustomGrouping, open])

  useEffect(() => {
    if (isEditingAssignmentGroups) return
    setAssignmentGroupsDraft(assignmentGroups.toString())
  }, [assignmentGroups, isEditingAssignmentGroups, open])

  useEffect(() => {
    if (open) return
    setDraggingCastId(null)
  }, [open])

  const visibleRange = useMemo(() => {
    const start = clamp(zoom?.start ?? 0, 0, computed.maxTime)
    const end = clamp(
      zoom?.end ?? computed.maxTime,
      start + 1,
      computed.maxTime,
    )
    return { start, end }
  }, [computed.maxTime, zoom?.end, zoom?.start])

  const phaseBoundaries = useMemo(
    () => computed.phaseDividers.map((phase) => phase.at),
    [computed.phaseDividers],
  )

  const updateCastOverride = useCallback(
    (
      castId: string,
      updater: (nextOverride: RaidPlanEncounterAssignmentCastOverride) => void,
      pushHistory: boolean,
    ) => {
      onUpdate((nextAbility) => {
        const nextOverrides = { ...(nextAbility.cast_overrides ?? {}) }
        const nextOverride = { ...(nextOverrides[castId] ?? {}) }
        updater(nextOverride)
        if (nextOverride.ignored === false) {
          delete nextOverride.ignored
        }
        if (
          nextOverride.time === undefined &&
          nextOverride.ignored !== true &&
          nextOverride.custom !== true
        ) {
          delete nextOverrides[castId]
        } else {
          nextOverrides[castId] = nextOverride
        }
        nextAbility.cast_overrides =
          Object.keys(nextOverrides).length > 0 ? nextOverrides : undefined
      }, pushHistory)
    },
    [onUpdate],
  )

  const removeCastOverride = useCallback(
    (castId: string, pushHistory: boolean) => {
      onUpdate((nextAbility) => {
        const nextOverrides = { ...(nextAbility.cast_overrides ?? {}) }
        delete nextOverrides[castId]
        nextAbility.cast_overrides =
          Object.keys(nextOverrides).length > 0 ? nextOverrides : undefined
      }, pushHistory)
    },
    [onUpdate],
  )

  const onAddCastAt = useCallback(
    (at: number) => {
      if (!canEdit) return
      const phaseIndex = phaseBoundaries.findIndex(
        (boundaryAt) => at < boundaryAt,
      )
      const resolvedPhaseIndex =
        phaseIndex >= 0 ? phaseIndex : Math.max(phaseBoundaries.length - 1, 0)
      const castId = createCastOverrideId()
      onUpdate((nextAbility) => {
        nextAbility.cast_overrides = {
          ...(nextAbility.cast_overrides ?? {}),
          [castId]: {
            custom: true,
            phase_index: resolvedPhaseIndex,
            time: Math.floor(at),
          },
        }
      }, true)
    },
    [canEdit, onUpdate, phaseBoundaries],
  )

  const castMarkers = useMemo(() => {
    if (!open) return []
    const defaultMarkerSourceEntries =
      computed.encounterAbilityRows.find(
        (encounterRow) => encounterRow.spellId === row.spellId,
      )?.entries ?? row.entries
    const defaultMarkers = defaultMarkerSourceEntries.flatMap((entry) => {
      const castId = entry.id
      const override = ability.cast_overrides?.[castId]
      if (override?.custom) return []
      const isIgnored = override?.ignored === true
      const phaseStart =
        entry.phaseIndex > 0 ? phaseBoundaries[entry.phaseIndex - 1] ?? 0 : 0
      const phaseEnd = phaseBoundaries[entry.phaseIndex] ?? computed.maxTime
      const phaseDragMax = Math.max(phaseStart, Math.ceil(phaseEnd) - 1)
      const markerAt =
        typeof override?.time === 'number' && Number.isFinite(override.time)
          ? Math.floor(clamp(override.time, phaseStart, phaseDragMax))
          : entry.time
      return [
        {
          id: castId,
          at: markerAt,
          ignored: isIgnored,
          label: formatTimelineTimestamp(markerAt),
          disableTooltip: draggingCastId === castId,
          tooltip: `Drag to adjust time, right click to ${
            isIgnored ? 'enable' : 'ignore'
          }`,
          onContextMenu: (event: React.MouseEvent<SVGRectElement>) => {
            event.preventDefault()
            updateCastOverride(
              castId,
              (nextOverride) => {
                nextOverride.ignored = isIgnored ? undefined : true
              },
              true,
            )
          },
          onPointerDown: (event: React.PointerEvent<SVGRectElement>) => {
            const svg = event.currentTarget.ownerSVGElement
            if (!svg || !canEdit) return
            event.preventDefault()
            event.stopPropagation()
            setDraggingCastId(castId)
            const rect = svg.getBoundingClientRect()
            const toTime = (clientX: number) => {
              const ratio = clamp((clientX - rect.left) / rect.width, 0, 1)
              const nextTime =
                visibleRange.start +
                ratio * (visibleRange.end - visibleRange.start)
              return Math.floor(clamp(nextTime, phaseStart, phaseDragMax))
            }
            const onMove = (moveEvent: PointerEvent) => {
              const nextTime = toTime(moveEvent.clientX)
              updateCastOverride(
                castId,
                (nextOverride) => {
                  nextOverride.time = nextTime
                },
                false,
              )
            }
            const onUp = (upEvent: PointerEvent) => {
              const nextTime = toTime(upEvent.clientX)
              updateCastOverride(
                castId,
                (nextOverride) => {
                  nextOverride.time = nextTime
                },
                false,
              )
              setDraggingCastId(null)
              onCommit()
              window.removeEventListener('pointermove', onMove)
              window.removeEventListener('pointerup', onUp)
            }
            window.addEventListener('pointermove', onMove)
            window.addEventListener('pointerup', onUp)
          },
        },
      ]
    })
    const customMarkers = Object.entries(ability.cast_overrides ?? {}).flatMap(
      ([castId, override]) => {
        if (!override.custom) return []
        const phaseIndex = override.phase_index
        if (
          typeof phaseIndex !== 'number' ||
          !Number.isInteger(phaseIndex) ||
          phaseIndex < 0
        ) {
          return []
        }
        const phaseStart =
          phaseIndex > 0 ? phaseBoundaries[phaseIndex - 1] ?? 0 : 0
        const phaseEnd = phaseBoundaries[phaseIndex] ?? computed.maxTime
        const phaseDragMax = Math.max(phaseStart, Math.ceil(phaseEnd) - 1)
        if (
          typeof override.time !== 'number' ||
          !Number.isFinite(override.time)
        ) {
          return []
        }
        const markerAt = Math.floor(
          clamp(override.time, phaseStart, phaseDragMax),
        )
        const isIgnored = override.ignored === true
        return [
          {
            id: castId,
            at: markerAt,
            ignored: isIgnored,
            label: formatTimelineTimestamp(markerAt),
            disableTooltip: draggingCastId === castId,
            tooltip: 'Drag to adjust time, right click to remove',
            onContextMenu: (event: React.MouseEvent<SVGRectElement>) => {
              event.preventDefault()
              removeCastOverride(castId, true)
            },
            onPointerDown: (event: React.PointerEvent<SVGRectElement>) => {
              const svg = event.currentTarget.ownerSVGElement
              if (!svg || !canEdit) return
              event.preventDefault()
              event.stopPropagation()
              setDraggingCastId(castId)
              const rect = svg.getBoundingClientRect()
              const toTime = (clientX: number) => {
                const ratio = clamp((clientX - rect.left) / rect.width, 0, 1)
                const nextTime =
                  visibleRange.start +
                  ratio * (visibleRange.end - visibleRange.start)
                return Math.floor(clamp(nextTime, phaseStart, phaseDragMax))
              }
              const onMove = (moveEvent: PointerEvent) => {
                const nextTime = toTime(moveEvent.clientX)
                updateCastOverride(
                  castId,
                  (nextOverride) => {
                    nextOverride.custom = true
                    nextOverride.phase_index = phaseIndex
                    nextOverride.time = nextTime
                  },
                  false,
                )
              }
              const onUp = (upEvent: PointerEvent) => {
                const nextTime = toTime(upEvent.clientX)
                updateCastOverride(
                  castId,
                  (nextOverride) => {
                    nextOverride.custom = true
                    nextOverride.phase_index = phaseIndex
                    nextOverride.time = nextTime
                  },
                  false,
                )
                setDraggingCastId(null)
                onCommit()
                window.removeEventListener('pointermove', onMove)
                window.removeEventListener('pointerup', onUp)
              }
              window.addEventListener('pointermove', onMove)
              window.addEventListener('pointerup', onUp)
            },
          },
        ]
      },
    )
    return [...defaultMarkers, ...customMarkers]
  }, [
    ability.cast_overrides,
    canEdit,
    computed.encounterAbilityRows,
    computed.maxTime,
    draggingCastId,
    open,
    removeCastOverride,
    onCommit,
    phaseBoundaries,
    row.entries,
    updateCastOverride,
    visibleRange.end,
    visibleRange.start,
  ])
  const activeCastMarkerCount = useMemo(
    () => castMarkers.filter((marker) => !marker.ignored).length,
    [castMarkers],
  )
  const inactiveCastMarkerCount = useMemo(
    () => castMarkers.filter((marker) => marker.ignored).length,
    [castMarkers],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type='button' variant='outline'>
          <Settings2Icon size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-5xl'>
        {open ? (
          <>
            <DialogHeader>
              <DialogTitle>Edit assignment ability</DialogTitle>
            </DialogHeader>
            <TimelineAbilitySection
              title={
                <span className='text-sm font-medium'>Click to add casts</span>
              }
              labels={
                <>
                  {computed.encounterAbilityRows.map((encounterRow) => (
                    <div
                      key={`${encounterRow.spellId}-${encounterRow.name}`}
                      className='flex items-center gap-2'
                    >
                      <a
                        href={`https://wowhead.com/spell=${encounterRow.spellId}`}
                        target='_blank'
                        rel='noreferrer'
                        className='w-40 max-w-40 shrink-0 h-5  text-muted-foreground truncate flex items-center gap-2'
                        style={{ color: encounterRow.color }}
                      >
                        <span className='truncate grow text-right'>
                          {encounterRow.name}
                        </span>
                        <SpellIcon
                          spellId={encounterRow.spellId}
                          className='w-5 h-5 rounded border border-border'
                        />
                      </a>
                    </div>
                  ))}
                </>
              }
              timeline={
                <TimelineContainer
                  maxTime={computed.maxTime}
                  phaseDividers={computed.phaseDividers}
                  phaseBoundaryHandles={computed.phaseBoundaryHandles}
                  rows={computed.encounterAbilityRows}
                  disableZoomInteraction={true}
                  castMarkers={castMarkers}
                  enableAddCastInteraction={canEdit}
                  onAddCastAt={onAddCastAt}
                  className='text-card'
                />
              }
            />

            {activeCastMarkerCount === 0 ? (
              <Alert>
                <AlertTitle>This ability has no casts</AlertTitle>
                <AlertDescription>
                  Click anywhere in the timeline above to add new casts
                  {inactiveCastMarkerCount > 0
                    ? ', or right click an inactive cast to enable it'
                    : ''}
                </AlertDescription>
              </Alert>
            ) : null}

            <FormGroup className='flex-wrap'>
              <div className='grow'>
                <Label>{t('dashboard.team.plans.assignments.title')}</Label>
                <Input
                  value={ability.title ?? defaultTitle}
                  onChange={(event) => {
                    const nextTitle = event.target.value
                    onUpdate((nextAbility) => {
                      nextAbility.title =
                        nextTitle.trim() !== '' && nextTitle !== defaultTitle
                          ? nextTitle
                          : undefined
                    }, false)
                  }}
                  onBlur={onCommit}
                  className='h-8'
                  placeholder={defaultTitle}
                  disabled={!canEdit}
                />
              </div>

              <div className='grow'>
                <Label>
                  {t('dashboard.team.plans.assignments.note-options')}
                </Label>
                <Input
                  value={ability.message ?? ''}
                  onChange={(event) =>
                    onUpdate((nextAbility) => {
                      nextAbility.message = event.target.value || undefined
                    }, false)
                  }
                  onBlur={onCommit}
                  className='h-8'
                  placeholder={`${getSoakPrefix(spellName)}${spellName}`}
                  disabled={!canEdit}
                />
              </div>

              <div className='grow'>
                <Label>
                  {t('dashboard.team.plans.assignments.note-tts-options')}
                </Label>
                <Input
                  value={ability.tts_message ?? ''}
                  onChange={(event) =>
                    onUpdate((nextAbility) => {
                      nextAbility.tts_message = event.target.value || undefined
                    }, false)
                  }
                  onBlur={onCommit}
                  className='h-8'
                  placeholder={`${getSoakPrefix(spellName)}${spellName}`}
                  disabled={!canEdit}
                />
              </div>

              <div>
                <Label>Color</Label>
                <ColorPicker
                  value={ability.rgb ?? row.color}
                  format='rgb'
                  onValueChange={(value) =>
                    onUpdate((nextAbility) => {
                      nextAbility.rgb = value
                    }, false)
                  }
                  onOpenChange={(open) => {
                    if (open) return
                    onCommit()
                  }}
                >
                  <ColorPickerTrigger asChild>
                    <Button variant='ghost' className='p-0 h-auto '>
                      <ColorPickerSwatch className='hover:border-muted-foreground transition-colors w-18.5 h-8' />
                    </Button>
                  </ColorPickerTrigger>
                  <ColorPickerContent>
                    <ColorPickerArea />
                    <ColorPickerHueSlider />
                    <ColorPickerInput />
                  </ColorPickerContent>
                </ColorPicker>
              </div>
            </FormGroup>

            <div className='space-y-2'>
              <div className='flex items-end gap-2'>
                <div className='space-y-1 flex-1'>
                  <div className='flex items-center gap-1'>
                    <span className=''>
                      {t('dashboard.team.plans.assignments.custom-grouping')}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='inline-flex items-center text-muted-foreground'>
                          <CircleHelp className='h-3.5 w-3.5' />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className='max-w-72'>
                        {t(
                          'dashboard.team.plans.assignments.custom-grouping-tooltip',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    value={customGroupingDraft}
                    onFocus={() => setIsEditingCustomGrouping(true)}
                    onChange={(event) => {
                      const nextValue = event.target.value
                      setCustomGroupingDraft(nextValue)
                      const sanitized = sanitizeCustomGroupingInput(nextValue)
                      const normalized = normalizeCustomGrouping(sanitized)
                      if (normalized || sanitized.trim() === '') {
                        onUpdate((nextAbility) => {
                          nextAbility.custom_grouping = normalized
                        }, false)
                      }
                    }}
                    onBlur={() => {
                      const sanitized =
                        sanitizeCustomGroupingInput(customGroupingDraft)
                      const normalized = normalizeCustomGrouping(sanitized)
                      setIsEditingCustomGrouping(false)
                      setCustomGroupingDraft(normalized ?? '')
                      onUpdate((nextAbility) => {
                        nextAbility.custom_grouping = normalized
                      }, false)
                      onCommit()
                    }}
                    placeholder={t(
                      'dashboard.team.plans.assignments.custom-grouping-placeholder',
                    )}
                    className='h-8'
                    disabled={!canEdit}
                  />
                </div>
                <div className='space-y-1 w-28'>
                  <span className=' text-muted-foreground'>
                    {t('dashboard.team.plans.assignments.groups-per-cast')}
                  </span>
                  <Input
                    type='text'
                    inputMode='numeric'
                    maxLength={2}
                    value={assignmentGroupsDraft}
                    onFocus={() => setIsEditingAssignmentGroups(true)}
                    onChange={(event) => {
                      const nextValue = event.target.value
                      setAssignmentGroupsDraft(nextValue)
                      const sanitized = sanitizeAssignmentGroupsInput(nextValue)
                      const normalized = normalizeAssignmentGroups(nextValue)
                      if (normalized || sanitized.trim() === '') {
                        onUpdate((nextAbility) => {
                          nextAbility.assignment_groups = normalized
                        }, false)
                      }
                    }}
                    onBlur={() => {
                      setIsEditingAssignmentGroups(false)
                      const normalized = normalizeAssignmentGroups(
                        assignmentGroupsDraft,
                      )
                      setAssignmentGroupsDraft(normalized?.toString() ?? '')
                      onUpdate((nextAbility) => {
                        nextAbility.assignment_groups = normalized
                      }, false)
                      onCommit()
                    }}
                    className='h-8'
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>

            <div className='justify-end flex gap-2'>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                Continue
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export default TeamPlanAssignmentDialog
